/**
 * Practitioners = deck creators / collaborators who get permanent free Cosmic
 * as a thank-you for their work (see getEffectiveTier — role 'practitioner').
 *
 * This roster is the single source of truth used to:
 *   (a) auto-assign the `practitioner` role to NEW signups in the Clerk webhook,
 *   (b) promote EXISTING accounts via apps/api/scripts/set-practitioner-role.ts, and
 *   (c) build the admin Practitioners roster + its stats (practitionerRoster()).
 *
 * Sarah is intentionally NOT here — she's an admin and the founder, not a
 * practitioner. She DOES author a deck (Four Weathers), which credits her and
 * links to her creator profile via `creatorSlug: 'sarah'` — but authorship alone
 * does not make someone a practitioner. Practitioner status is what grants the
 * permanent free Cosmic comp, which is meaningless for an admin, and it is what
 * puts a person on "our practitioners" surfaces. Because practitionerRoster()
 * builds its pool from THIS list, her deck's pulls count as house pulls and
 * cannot dilute Adrienne's or Jasleni's profit share.
 *
 * Any surface that lists "our practitioners" must read from HERE rather than
 * keeping its own copy; the admin view used to hardcode its own list and drifted
 * to showing Sarah while omitting Jasleni.
 *
 * `slug` is the join key across the codebase — it matches both
 * DeckConfig.creatorSlug (deck-registry.ts) and Creator.id (web creators.ts),
 * so a practitioner's decks resolve by lookup instead of name-matching.
 *
 * This is a small hardcoded allowlist because practitioners are few and known.
 * If we later build an admin UI to comp arbitrary users, graduate this to a
 * DB-backed table (mirroring beta_tester_allowlist).
 */
export interface Practitioner {
  /** Join key — matches DeckConfig.creatorSlug and web Creator.id. */
  slug: string;
  /** Account email, lowercase. Keys the role allowlist and the users lookup. */
  email: string;
  fullName: string;
  /** Short role line for admin surfaces. */
  role: string;
}

export const PRACTITIONERS: readonly Practitioner[] = [
  {
    slug: "adrienne",
    email: "adrienne@breathofgold.com",
    fullName: "Adrienne Rivera",
    role: "Breathwork · Breath of Gold®",
  },
  {
    slug: "jasleni",
    email: "info@jaslenidesigns.com",
    fullName: "Jasleni Brito",
    role: "Latina artist · Confidence coach",
  },
];

/** Allowlist of practitioner emails, derived from the roster above. */
export const PRACTITIONER_EMAILS: readonly string[] = PRACTITIONERS.map(
  (p) => p.email,
);

/** Case-insensitive membership test for the practitioner allowlist. */
export function isPractitionerEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return PRACTITIONER_EMAILS.includes(email.trim().toLowerCase());
}

/** Look up a practitioner by their join slug (deck `creatorSlug`). */
export function practitionerBySlug(
  slug: string | null | undefined,
): Practitioner | undefined {
  if (!slug) return undefined;
  return PRACTITIONERS.find((p) => p.slug === slug);
}

/** Look up a practitioner by account email (case-insensitive). */
export function practitionerByEmail(
  email: string | null | undefined,
): Practitioner | undefined {
  if (!email) return undefined;
  const needle = email.trim().toLowerCase();
  return PRACTITIONERS.find((p) => p.email === needle);
}
